/*eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
import { type FC, useMemo } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { usePlaylistAutoPlay } from '../../hooks/usePlaylistAutoPlay';
import type { PlaylistItemData } from '../playlist/PlaylistItem';

type AutoPlayManagerProps = {
	cloudId: string;
};

export const AutoPlayManager: FC<AutoPlayManagerProps> = ({ cloudId }) => {
	const { activeContent } = useAudioPlayerStore();

	//Atlassian rules are that fragment spreads can't be split between modules, however
	//these are currently only split between folders in the same module?
	//since modifyPlaylistButton may exist outside the module it may make sense to have a full copy
	// of this query inside that component as lint desires?
	const data = useLazyLoadQuery<any>(
		graphql`
			query AutoPlayManagerPlaylistQuery($cloudId: ID!) {
				confluence_getPlaylist(cloudId: $cloudId) {
					tracks {
						nodes {
							id
							type
							mainSource
							mainContent {
								id
								title
								type
								status
								space {
									key
								}
								history {
									createdBy {
										displayName
										profilePicture {
											path
										}
									}
								}
								links {
									webui
									base
								}
							}
							supportingContent {
								ari
								links {
									webui
									base
								}
							}
						}
					}
				}
			}
		`,
		{ cloudId },
		{
			fetchPolicy: 'store-or-network',
		},
	);

	const playlistItems: PlaylistItemData[] = useMemo(() => {
		const tracks = (data as any).confluence_getPlaylist?.tracks?.nodes || [];

		return tracks
			.filter((track: any) => {
				const mainContent = track.mainContent;
				const hasMainContent = !!mainContent;
				const hasValidStatus = mainContent?.status === 'current' || mainContent?.status === 'draft';
				return hasMainContent && hasValidStatus;
			})
			.map((track: any) => {
				const mainContent = track.mainContent;
				const supportingContent = track.supportingContent;
				const creator = mainContent.history?.createdBy;
				const avatarUrl = creator?.profilePicture?.path || undefined;

				return {
					id: mainContent.id,
					title: mainContent.title || undefined,
					trackType: track.type,
					author: creator?.displayName || undefined,
					avatarUrl,
					spaceKey: mainContent.space?.key || '',
					contentType: mainContent.type || '',
					contentId: mainContent.id,
					mainContent: {
						ari: track.mainSource,
						link: mainContent.links?.base + mainContent.links?.webui || '',
					},
					supportingContent: supportingContent?.length > 0 ? supportingContent.map((content: any) => ({
						ari: content.ari,
						link: content.links?.base + content.links?.webui || '',
					})) : [],
				};
			});
	}, [data]);

	// Enable auto-play of next track when current track ends
	usePlaylistAutoPlay({
		playlistItems,
		currentItemId: activeContent?.contentId,
	});

	return null;
};
